
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Shop } from '@/lib/supabase/shops/types';
import { Product } from '@/lib/supabase/products/types';
import { getShops } from '@/lib/supabase/shops';
import { getProducts } from '@/lib/supabase/products';
import { PageViewTracker } from '@/components/dashboard/shops/PageViewTracker';
import ShopHeader from '@/components/shop/ShopHeader';
import ShopFooter from '@/components/shop/ShopFooter';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Grid2X2, List, Tag } from 'lucide-react';

const ShopCategoriesPage = () => {
  const { shopSlug } = useParams<{ shopSlug: string }>();
  const [shop, setShop] = useState<Shop | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Set the document title dynamically
  useDocumentTitle(shop?.name ? `Catégories - ${shop.name} | Scaloo` : 'Catégories | Scaloo');
  
  useEffect(() => {
    const fetchShopAndProducts = async () => {
      if (!shopSlug) return;
      
      try {
        setLoading(true);
        // Fetch all shops and find the one matching the slug
        const allShops = await getShops();
        const foundShop = allShops.find(s => 
          (s.slug && s.slug.toLowerCase() === shopSlug.toLowerCase()) || 
          (s.name && s.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') === shopSlug.toLowerCase())
        );
        
        if (foundShop) {
          setShop(foundShop);
          
          // Fetch products for this shop
          const shopProducts = await getProducts(foundShop.id);
          setProducts(shopProducts);
          
          // Extract unique categories
          const uniqueCategories = Array.from(
            new Set(
              shopProducts
                .map(product => product.category)
                .filter(category => category) // Filter out undefined/null/empty categories
            )
          ) as string[];
          
          // Add "Tous" category at the beginning
          setCategories(['Tous', ...uniqueCategories]);
        }
      } catch (error) {
        console.error('Error fetching shop data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchShopAndProducts();
  }, [shopSlug]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-32 h-6 bg-gray-300 dark:bg-gray-700 rounded mb-4"></div>
          <div className="w-48 h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }
  
  if (!shop) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-semibold mb-4">Boutique introuvable</h1>
        <p className="mb-6 text-gray-600 dark:text-gray-400">
          Nous n'avons pas trouvé la boutique que vous recherchez.
        </p>
        <Link to="/">
          <Button>Retour à l'accueil</Button>
        </Link>
      </div>
    );
  }
  
  const filterProductsByCategory = (category: string) => {
    if (category === 'Tous') {
      return products;
    }
    return products.filter(product => product.category === category);
  };
  
  return (
    <PageViewTracker shopId={shop.id} organizationId={shop.organization_id}>
      <div className="flex flex-col min-h-screen">
        <ShopHeader shop={shop} />
        
        <main className="flex-grow pt-16">
          {/* Hero section with shop information */}
          <section className="bg-gradient-to-b from-indigo-100 to-white dark:from-gray-800 dark:to-gray-900 py-8 md:py-12">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  {shop.logo_url ? (
                    <img 
                      src={shop.logo_url} 
                      alt={`${shop.name} logo`} 
                      className="h-12 mr-4 rounded-lg shadow-sm"
                    />
                  ) : (
                    <div className="h-12 w-12 mr-4 bg-indigo-600 rounded-lg flex items-center justify-center text-white text-xl font-bold">
                      {shop.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                      Catégories - {shop.name}
                    </h1>
                    <p className="text-gray-700 dark:text-gray-300">
                      Découvrez tous nos produits par catégorie
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button 
                    variant={viewMode === 'grid' ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={() => setViewMode('grid')}
                    className="flex items-center"
                  >
                    <Grid2X2 className="h-4 w-4 mr-2" />
                    Grille
                  </Button>
                  <Button 
                    variant={viewMode === 'list' ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={() => setViewMode('list')}
                    className="flex items-center"
                  >
                    <List className="h-4 w-4 mr-2" />
                    Liste
                  </Button>
                </div>
              </div>
            </div>
          </section>
          
          {/* Categories Section */}
          <section className="py-8 bg-white dark:bg-gray-900">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <Tabs defaultValue="Tous" className="w-full">
                <div className="mb-6 border-b border-gray-200 dark:border-gray-800">
                  <TabsList className="flex overflow-x-auto pb-2 space-x-2">
                    {categories.map((category) => (
                      <TabsTrigger 
                        key={category} 
                        value={category}
                        className="flex items-center"
                      >
                        {category === 'Tous' ? (
                          <span>Tous les produits</span>
                        ) : (
                          <>
                            <Tag className="h-4 w-4 mr-2" />
                            {category}
                          </>
                        )}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>
                
                {categories.map((category) => (
                  <TabsContent key={category} value={category} className="pt-4">
                    <h2 className="text-xl font-semibold mb-6 text-gray-900 dark:text-white">
                      {category === 'Tous' ? 'Tous les produits' : `Catégorie : ${category}`}
                    </h2>
                    
                    {filterProductsByCategory(category).length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-gray-600 dark:text-gray-400">
                          Aucun produit disponible dans cette catégorie.
                        </p>
                      </div>
                    ) : viewMode === 'grid' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filterProductsByCategory(category).map((product) => {
                          // Create a slug from the product name
                          const productSlug = product.name ? 
                            product.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : '';
                          
                          // Format URL for the product
                          const productUrl = `/shop/${shopSlug}/products/${productSlug}-${product.id}`;
                          
                          return (
                            <Card key={product.id} className="hover:shadow-lg transition-shadow duration-300">
                              <CardHeader className="p-0">
                                {product.media_urls && product.media_urls.length > 0 ? (
                                  <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                                    <img 
                                      src={product.media_urls[0]} 
                                      alt={product.name} 
                                      className="w-full h-full object-cover object-center transition-transform hover:scale-105"
                                    />
                                  </div>
                                ) : (
                                  <div className="aspect-video w-full bg-gray-200 dark:bg-gray-700 rounded-t-lg flex items-center justify-center">
                                    <span className="text-gray-500 dark:text-gray-400">Pas d'image</span>
                                  </div>
                                )}
                              </CardHeader>
                              <CardContent className="p-4">
                                <CardTitle className="mb-2 text-lg">{product.name}</CardTitle>
                                <p className="text-gray-700 dark:text-gray-300 line-clamp-2 mb-3 text-sm">
                                  {product.description || 'Aucune description disponible'}
                                </p>
                                <p className="font-bold text-lg text-indigo-600 dark:text-indigo-400">
                                  {product.price ? `${product.price} ${product.currency || '€'}` : 'Prix sur demande'}
                                </p>
                              </CardContent>
                              <CardFooter className="px-4 pb-4 pt-0">
                                <Link to={productUrl} className="w-full">
                                  <Button className="w-full" variant="default">
                                    Voir le produit
                                  </Button>
                                </Link>
                              </CardFooter>
                            </Card>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {filterProductsByCategory(category).map((product) => {
                          // Create a slug from the product name
                          const productSlug = product.name ? 
                            product.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : '';
                          
                          // Format URL for the product
                          const productUrl = `/shop/${shopSlug}/products/${productSlug}-${product.id}`;
                          
                          return (
                            <Card key={product.id} className="overflow-hidden">
                              <div className="flex flex-col md:flex-row">
                                <div className="md:w-1/4">
                                  {product.media_urls && product.media_urls.length > 0 ? (
                                    <div className="h-full w-full">
                                      <img 
                                        src={product.media_urls[0]} 
                                        alt={product.name} 
                                        className="h-48 md:h-full w-full object-cover object-center"
                                      />
                                    </div>
                                  ) : (
                                    <div className="h-48 md:h-full w-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                      <span className="text-gray-500 dark:text-gray-400">Pas d'image</span>
                                    </div>
                                  )}
                                </div>
                                <div className="p-4 md:p-6 md:w-3/4 flex flex-col">
                                  <div className="flex-grow">
                                    <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{product.name}</h3>
                                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                                      {product.description || 'Aucune description disponible'}
                                    </p>
                                    <p className="font-bold text-lg text-indigo-600 dark:text-indigo-400 mb-4">
                                      {product.price ? `${product.price} ${product.currency || '€'}` : 'Prix sur demande'}
                                    </p>
                                  </div>
                                  <div className="mt-4">
                                    <Link to={productUrl}>
                                      <Button className="w-full md:w-auto" variant="default">
                                        Voir le produit
                                      </Button>
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </section>
        </main>
        
        <ShopFooter shop={shop} shopSlug={shopSlug} />
      </div>
    </PageViewTracker>
  );
};

export default ShopCategoriesPage;
